import type React from "react";
import {useCallback, useMemo, useState} from "react";
import {observer} from "mobx-react-lite";
import {generatePath, useNavigate} from "react-router";
import {Alert, Button, InputAdornment, Skeleton, TextField, ToggleButton, ToggleButtonGroup} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import {routes} from "~/routing/routes";
import {useStores} from "~/stores";
import {useCollapsiblePanel} from "~/components/resizable-split/useCollapsiblePanel";
import {SupersetDashboardPanel} from "~/pages/chat-agent/components/SupersetDashboardPanel/SupersetDashboardPanel";
import type {EmbeddedDashboardView} from "~/pages/chat-agent/types/chatTypes";
import {embeddedDashboardKey} from "~/pages/chat-agent/utils/embeddedDashboardKey";
import type {DashboardItem} from "./services/dashboardsApi";
import {useHermesDashboards, useInvalidateMyDashboards, useMyDashboards} from "./services/dashboardsQueries";
import {AtlasDashboardPanel} from "./AtlasDashboardPanel";
import {PageHeader} from "~/components/main/header/PageHeader";
import {DashboardDetailLayout} from "./DashboardDetailLayout";
import {DashboardsAtlasSplit} from "./DashboardsAtlasSplit";
import {DashboardsEmptyState} from "./DashboardsEmptyState";
import {GallerySection} from "./GallerySection";
import {DeleteDashboardDialog} from "./DeleteDashboardDialog";
import {RenameDashboardDialog} from "./RenameDashboardDialog";
import {filterGallery, GALLERY_FILTERS, type GalleryFilter} from "./dashboards.utils";
import "./dashboards.scss";

const GallerySkeleton: React.FC = () => (
    <section className="dsh-sec">
        <Skeleton variant="text" width={180} height={24}/>
        <div className="dsh-ggrid">
            {Array.from({length: 4}, (_, i) => <Skeleton key={i} variant="rounded" height={200} sx={{borderRadius: "12px"}}/>)}
        </div>
    </section>
);

export const DashboardsGallery: React.FC = observer(() => {
    const navigate = useNavigate();
    const {bagStore} = useStores();
    const bagId = bagStore.bagId;
    const hermes = useHermesDashboards();
    const mine = useMyDashboards(bagId);
    const invalidateMine = useInvalidateMyDashboards();
    const refreshMine = useCallback(() => invalidateMine(bagId), [bagId, invalidateMine]);

    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState<GalleryFilter>("All");
    const [renameTarget, setRenameTarget] = useState<DashboardItem | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<DashboardItem | null>(null);
    // Deleted dashboards leave the list at once, before the refetch lands.
    const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(new Set());
    const [localTitles, setLocalTitles] = useState<Record<string, string>>({});

    const builtIn = useMemo(() => hermes.data?.dashboards ?? [], [hermes.data]);
    const myDashboards = useMemo(
        () => (mine.data?.dashboards ?? [])
            .filter(d => !deletedIds.has(d.id))
            .map(d => localTitles[d.id] ? {...d, title: localTitles[d.id]} : d),
        [mine.data, deletedIds, localTitles],
    );
    const sections = filterGallery(builtIn, myDashboards, query, filter);
    const searching = Boolean(query.trim());

    const isLoading = hermes.isPending || (Boolean(bagId) && mine.isPending);
    const unavailable = !isLoading && !hermes.isError && !mine.isError
        && !hermes.data?.available && !mine.data?.available;

    const openDashboard = (d: DashboardItem) => {
        navigate(generatePath(routes.dashboards_detail, {id: String(bagId), source: d.source, dashboardId: d.id}));
    };

    // "+ New dashboard" docks a fresh DashGen chat on the left, like the chat route; what it builds
    // opens on the right in place of the gallery in the chat route's own dashboard panel, and lands
    // in Yours. Its full screen collapses Atlas; closing Atlas brings the gallery back.
    const [atlasOpen, setAtlasOpen] = useState(false);
    const [built, setBuilt] = useState<EmbeddedDashboardView | null>(null);
    const atlasPanel = useCollapsiblePanel();
    const newDashboard = () => setAtlasOpen(true);
    const closeAtlas = () => {
        setAtlasOpen(false);
        setBuilt(null);
        atlasPanel.setCollapsed(false);
    };
    const onDashboardChanged = useCallback((dashboard: EmbeddedDashboardView) => {
        setBuilt(dashboard);
        void refreshMine();
    }, [refreshMine]);

    const onRenamed = (d: DashboardItem, title: string) => {
        setLocalTitles(t => ({...t, [d.id]: title}));
        setRenameTarget(null);
        void refreshMine();
    };

    const onDeleted = (d: DashboardItem) => {
        setDeletedIds(ids => new Set(ids).add(d.id));
        setDeleteTarget(null);
        void refreshMine();
    };

    return (
        <DashboardsAtlasSplit
            atlas={atlasOpen ? (
                <AtlasDashboardPanel
                    mode={{kind: "new"}}
                    onClose={closeAtlas}
                    onDashboardChanged={onDashboardChanged}
                />
            ) : null}
            atlasPanel={built ? atlasPanel : undefined}
        >
            {built ? (
                <DashboardDetailLayout onBack={closeAtlas} title={built.title}>
                    <SupersetDashboardPanel
                        // Remounts (reloading the iframe) when DashGen edits it: the embed uuid stays the same.
                        key={embeddedDashboardKey(built)}
                        dashboard={built}
                        showTitle={false}
                        isFullscreen={atlasPanel.collapsed}
                        onToggleFullscreen={atlasPanel.toggle}
                    />
                </DashboardDetailLayout>
            ) : <div className="dsh-root dsh-page">
                {/* The page title lives in the app's top bar, as on Supplier 360. */}
                <PageHeader title="Dashboards"/>

                {/* One row, left to right: search · show filter · New dashboard. */}
                <div className="dsh-filters">
                    <TextField
                        size="small"
                        placeholder="Search dashboards"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        className="dsh-search"
                        slotProps={{
                            htmlInput: {"aria-label": "Search dashboards"},
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>
                                ),
                            },
                        }}
                    />
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        className="dsh-seg"
                        value={filter}
                        onChange={(_, v: GalleryFilter | null) => v && setFilter(v)}
                        aria-label="Show"
                    >
                        {GALLERY_FILTERS.map(f => <ToggleButton key={f} value={f}>{f}</ToggleButton>)}
                    </ToggleButtonGroup>
                    <Button
                        variant="contained"
                        className="dsh-btn-primary"
                        startIcon={<AddIcon/>}
                        onClick={newDashboard}
                        disabled={!bagId || atlasOpen}
                    >
                        New dashboard
                    </Button>
                </div>

                {(hermes.isError || mine.isError) && (
                    <Alert severity="error" className="dsh-alert">
                        Some dashboards could not be loaded. {(hermes.error ?? mine.error)?.message}
                    </Alert>
                )}

                {isLoading ? <GallerySkeleton/> : unavailable ? (
                    <DashboardsEmptyState variant="page" icon title="Dashboards are not enabled for this environment"/>
                ) : sections.noMatch ? (
                    <DashboardsEmptyState
                        variant="page"
                        icon
                        title={`No dashboards match “${query.trim()}”`}
                        action={(
                            <Button size="small" variant="outlined" onClick={() => {
                                setQuery("");
                                setFilter("All");
                            }}>Clear search</Button>
                        )}
                    />
                ) : <>
                    {sections.showBuiltIn && (
                        <GallerySection
                            id="dsh-sec-builtin"
                            title="Built-in dashboards"
                            subtitle="Maintained by Mithra. View only."
                            dashboards={sections.builtIn}
                            emptyText={searching ? "No built-in dashboards match." : "No built-in dashboards have been published yet."}
                            onOpen={openDashboard}
                        />
                    )}
                    {sections.showMine && (
                        <GallerySection
                            id="dsh-sec-mine"
                            title="Your dashboards"
                            subtitle="Built by you. Open one and edit it with Atlas."
                            dashboards={sections.mine}
                            emptyText={searching ? "None of your dashboards match." : "You haven't built a dashboard yet."}
                            emptyAction={!searching && bagId > 0 && (
                                <Button size="small" variant="outlined" onClick={newDashboard}>New dashboard</Button>
                            )}
                            onOpen={openDashboard}
                            onRename={setRenameTarget}
                            onDelete={setDeleteTarget}
                        />
                    )}
                </>}

                <RenameDashboardDialog target={renameTarget} onClose={() => setRenameTarget(null)} onRenamed={onRenamed}/>
                <DeleteDashboardDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={onDeleted}/>
            </div>}
        </DashboardsAtlasSplit>
    );
});
